import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, fmtErr, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import ImagePicker from "@/components/ImagePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const empty = { nama: "", jabatan: "", photo: [], order: 0 };

export default function PengurusTab() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/pengurus").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (item) => {
    setForm(item ? { nama: item.nama, jabatan: item.jabatan, photo: item.photo ? [item.photo] : [], order: item.order } : empty);
    setDlg(item || "new");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { nama: form.nama, jabatan: form.jabatan, photo: form.photo[0] || "", order: Number(form.order) || 0 };
    try {
      if (dlg === "new") await api.post("/pengurus", payload);
      else await api.put(`/pengurus/${dlg.id}`, payload);
      toast.success(t("common.saved"));
      setDlg(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/pengurus/${id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="pengurus-tab">
      <Button onClick={() => open(null)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 mb-6" data-testid="pengurus-add-button">
        <Plus className="h-4 w-4 mr-2" /> {t("common.add")}
      </Button>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-5 flex flex-col items-center text-center" data-testid={`pengurus-item-${p.id}`}>
            {p.photo ? (
              <img src={imgUrl(p.photo)} alt={p.nama} className="h-20 w-20 rounded-full object-cover border-2 border-[#D4AF37]" />
            ) : (
              <span className="h-20 w-20 rounded-full bg-emerald-800 text-white text-lg font-bold flex items-center justify-center border-2 border-[#D4AF37]">
                {p.nama.replace(/^(H\.|Hj\.|Ust\.|Ustdzh\.)\s*/g, "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </span>
            )}
            <h4 className="text-sm font-bold text-emerald-950 mt-3">{p.nama}</h4>
            <p className="text-xs text-slate-500">{p.jabatan} · #{p.order}</p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => open(p)} data-testid={`pengurus-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="destructive" onClick={() => remove(p.id)} data-testid={`pengurus-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl text-emerald-950">{t("dash.pengurus")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="pengurus-form">
            <div>
              <Label className="text-sm font-medium text-slate-700">{t("auth.name")}</Label>
              <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required className="mt-1.5" data-testid="pengurus-form-nama" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Jabatan</Label>
                <Input value={form.jabatan} onChange={(e) => setForm({ ...form, jabatan: e.target.value })} required className="mt-1.5" data-testid="pengurus-form-jabatan" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Urutan</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1.5" data-testid="pengurus-form-order" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Pas Foto ({t("common.optional")})</Label>
              <div className="mt-1.5"><ImagePicker values={form.photo} onChange={(photo) => setForm({ ...form, photo })} max={1} testId="pengurus-photo-picker" /></div>
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="pengurus-form-submit">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
